/**
 * CV Download Utility with Multi-language Support
 *
 * Handles CV download functionality in multiple languages.
 * Supports: English, Chinese, Malay, Tamil, Thai
 */
import i18n from '../src/i18n'
export interface CVData {
  personalInfo: {
    fullName: string
    dateOfBirth: string
    nationality: string
    contact: string
    email: string
    location: string
  }
  summary: string
  licenses: Array<{
    title: string
    details: string
  }>
  education: {
    degree: string
    institution: string
    year: string
  }
  experience: Array<{
    title: string
    organization: string
    location: string
    period: string
    responsibilities: string[]
    achievements?: string[]
  }>
  certifications: Array<{
    category: string
    items: string[]
  }>
  skills: {
    clinical: string[]
    technical: string[]
    soft: string[]
  }
  languages: Array<{
    language: string
    proficiency: string
  }>
}
// Base CV data (used as fallback)
export const cvData: CVData = {
  personalInfo: {
    fullName: 'Sudarat Bunthawee',
    dateOfBirth: '31 December 1997',
    nationality: 'Thai / Finnish',
    contact: '+358 458426661',
    email: 'mona.sudarat31@gmail.com',
    location: 'Lahti, Finland',
  },
  summary:
    "Dedicated Registered Nurse with Finnish healthcare training and active nursing licenses. Expertise in surgical, psychiatric, geriatric, and emergency care. Proven ability in evidence-based practice, patient safety, and multidisciplinary collaboration. Seeking to contribute clinical excellence and cultural adaptability to Singapore's healthcare system.",
  licenses: [
    {
      title: 'Finnish Nursing License',
      details:
        'Licensed Registered Nurse (Sairaanhoitaja) | Registration Number: 10009985648 | Issued by: Valvira | Status: Active',
    },
    {
      title: 'Finnish Medication License',
      details:
        'License Type: Love (Lääkkeenmääräämis Lupa hoitajille) | Status: Valid',
    },
  ],
  education: {
    degree: 'Bachelor of Health Care (Nursing Science)',
    institution: 'LAB University of Applied Sciences, Lahti, Finland',
    year: '2025',
  },
  experience: [
    {
      title: 'Nursing Student - Acute Care Unit',
      organization: 'Päijät-Häme Central Hospital',
      location: 'Lahti, Finland',
      period: 'February 2025 - April 2025',
      responsibilities: [
        'Provided comprehensive patient care in acute care settings, including vital signs monitoring, medication administration, and documentation in electronic health records (LifeCare)',
        'Performed clinical procedures such as IV cannulation, ECG recordings, and various patient care interventions',
        'Collaborated with multidisciplinary teams including physicians, physiotherapists, and pharmacists',
        'Assessed patient conditions, responded to changes in vital signs, and escalated concerns appropriately',
      ],
    },
    {
      title: 'Nursing Student - Operating Theatre',
      organization: 'Etelä-Karjala Central Hospital',
      location: 'Lappeenranta, Finland',
      period: 'January 2025 - February 2025',
      responsibilities: [
        'Gained comprehensive experience in perioperative nursing care across all three roles: anesthesia nurse, scrub nurse, and circulating nurse',
        'Assisted in surgical procedures while maintaining strict aseptic technique and sterile field protocols',
        "Monitored patients' vital signs and anesthesia responses using advanced monitoring equipment",
      ],
    },
    {
      title: 'Registered Nurse - Surgical Ward',
      organization: 'Päijät-Häme Central Hospital, Ward R5',
      location: 'Lahti, Finland',
      period: 'December 2024 - February 2025',
      responsibilities: [
        'Provided comprehensive pre-operative and post-operative nursing care for surgical patients in a 30-bed acute care ward',
        'Administered medications, IV therapy, and pain management according to physician orders and nursing protocols',
        'Monitored patient vital signs, wound healing, and post-surgical recovery',
      ],
      achievements: [
        'Successfully managed care for high-acuity post-surgical patients requiring complex wound care',
        'Maintained 100% medication administration accuracy through careful verification and documentation',
      ],
    },
  ],
  certifications: [
    {
      category: 'Psychiatric & Mental Health',
      items: [
        'Anxious and Sleepless Youth: Adolescent Psychiatric Perspectives (55 min) - Nov 2024',
        'Motivational Interviewing (60 min) - Dec 2022',
      ],
    },
    {
      category: 'Geriatric & Elderly Care',
      items: [
        'Gerasthenia Identification (30 min) - Oct 2024',
        'Elderly Medication Management (60 min) - Sept 2024',
        'Polypharmacy in the Elderly (40 min) - Sept 2024',
        'Fall Prevention (60 min) - Sept 2024',
      ],
    },
    {
      category: 'Acute & Emergency Care',
      items: [
        'Neurological Patient Emergency Assessment (60 min) - Nov 2023',
        'Systematic Assessment of Vital Functions Using ABCDE Principles (30 min) - Oct 2023',
        'Central Venous Catheter (CVK) Insertion and Use (70 min) - Nov 2023',
      ],
    },
  ],
  skills: {
    clinical: [
      'Comprehensive patient assessment and vital signs monitoring',
      'Advanced wound care and dressing changes',
      'Medication administration (oral, IV, IM, SC)',
      'IV cannulation, catheterization, and NG tube management',
      'Pain assessment and management',
      'Pre-operative and post-operative patient care',
    ],
    technical: [
      'LifeCare Electronic Health Record (EHR) system',
      'Medical equipment operation (vital signs monitors, infusion pumps, oxygen delivery systems)',
      'Infection control protocols and sterile techniques',
      'Emergency response protocols and basic life support',
    ],
    soft: [
      'Excellent communication and active listening skills',
      'Strong empathy and patient-centered care',
      'Effective team collaboration in multidisciplinary settings',
      'Time management in high-pressure environments',
      'Cultural sensitivity with diverse patient populations',
    ],
  },
  languages: [
    { language: 'Thai', proficiency: 'Native' },
    { language: 'Finnish', proficiency: 'Fluent' },
    { language: 'English', proficiency: 'Professional (B2)' },
  ],
}
/**
 * Generate a text-based CV file for download with multi-language support
 */
const generateTextCV = (language: string = 'en'): string => {
  // Get translations for the selected language
  const t = (key: string, options?: any) => {
    return i18n.t(key, { lng: language, ...options })
  }
  const divider = '='.repeat(80)
  const subDivider = '-'.repeat(80)
  let cv = `${t('cv.title')}
${divider}
${t('cv.sections.personalInfo')}
${subDivider}
${t('cv.personalInfo.fullName')}: ${cvData.personalInfo.fullName}
${t('cv.personalInfo.dateOfBirth')}: ${cvData.personalInfo.dateOfBirth}
${t('cv.personalInfo.nationality')}: ${cvData.personalInfo.nationality}
${t('cv.personalInfo.contact')}: ${cvData.personalInfo.contact}
${t('cv.personalInfo.email')}: ${cvData.personalInfo.email}
${t('cv.personalInfo.location')}: ${cvData.personalInfo.location}
${t('cv.sections.summary')}
${subDivider}
${t('cv.summary')}
${t('cv.sections.licenses')}
${subDivider}
${cvData.licenses
  .map(
    (
      license,
    ) => `${t('cv.licenses.' + (license.title.includes('Nursing') ? 'nursing' : 'medication') + '.title')}
${t('cv.licenses.' + (license.title.includes('Nursing') ? 'nursing' : 'medication') + '.details')}`,
  )
  .join('\n\n')}
${t('cv.sections.education')}
${subDivider}
${t('cv.education.degree')}
${t('cv.education.institution')}
${t('cv.education.year')}: ${cvData.education.year}
${t('cv.sections.experience')}
${subDivider}
${cvData.experience
  .map(
    (exp, index) => `
${t(`cv.experience.${index}.title`)}
${t(`cv.experience.${index}.organization`)}, ${t(`cv.experience.${index}.location`)}
${t(`cv.experience.${index}.period`)}
${t('cv.experience.responsibilities')}:
${t(`cv.experience.${index}.responsibilities`, { returnObjects: true })
  .map((r: string) => `• ${r}`)
  .join('\n')}
${
  exp.achievements
    ? `\n${t('cv.experience.achievements')}:\n${t(
        `cv.experience.${index}.achievements`,
        { returnObjects: true },
      )
        .map((a: string) => `• ${a}`)
        .join('\n')}`
    : ''
}
`,
  )
  .join('\n' + subDivider + '\n')}
${t('cv.sections.certifications')}
${subDivider}
${cvData.certifications
  .map(
    (cert, index) => `
${t(`cv.certifications.${index}.category`)}:
${t(`cv.certifications.${index}.items`, { returnObjects: true })
  .map((item: string) => `• ${item}`)
  .join('\n')}
`,
  )
  .join('\n')}
${t('cv.sections.skills')}
${subDivider}
${t('cv.skills.clinical.title')}:
${t('cv.skills.clinical.items', { returnObjects: true })
  .map((s: string) => `• ${s}`)
  .join('\n')}
${t('cv.skills.technical.title')}:
${t('cv.skills.technical.items', { returnObjects: true })
  .map((s: string) => `• ${s}`)
  .join('\n')}
${t('cv.skills.soft.title')}:
${t('cv.skills.soft.items', { returnObjects: true })
  .map((s: string) => `• ${s}`)
  .join('\n')}
${t('cv.sections.languages')}
${subDivider}
${cvData.languages.map((lang) => `${t(`cv.languages.${lang.language.toLowerCase()}`)}: ${t(`cv.languageProficiency.${lang.proficiency.toLowerCase().replace(/\s+/g, '_').replace(/[()]/g, '')}`)}`).join('\n')}
${divider}
${t('cv.generated')}: ${new Date().toLocaleDateString(language === 'zh' ? 'zh-CN' : language === 'ms' ? 'ms-MY' : language === 'ta' ? 'ta-IN' : language === 'th' ? 'th-TH' : 'en-GB')}
`
  return cv
}
/**
 * Trigger CV download
 * Creates a text file and triggers browser download
 */
export const downloadCV = (language: string = 'en'): void => {
  try {
    // Generate CV content in selected language
    const cvContent = generateTextCV(language)
    // Create blob
    const blob = new Blob([cvContent], { type: 'text/plain;charset=utf-8' })
    // Create download link
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    // Language-specific filename
    const languageCode = language.toUpperCase()
    link.download = `Sudarat_Bunthawee_CV_${languageCode}_${new Date().getFullYear()}.txt`
    // Trigger download
    document.body.appendChild(link)
    link.click()
    // Cleanup
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
    console.log(`CV download initiated successfully in ${language}`)
  } catch (error) {
    console.error('Error downloading CV:', error)
    // Fallback: Open in new tab
    const cvContent = generateTextCV(language)
    const newWindow = window.open()
    if (newWindow) {
      newWindow.document.write(`<pre>${cvContent}</pre>`)
      newWindow.document.title = 'Sudarat Bunthawee - Curriculum Vitae'
    }
  }
}
/**
 * Alternative: Open hosted PDF (if you upload one)
 * Replace the URL with your actual hosted PDF location
 */
export const openHostedCV = (): void => {
  // TODO: Replace with actual PDF URL once uploaded
  const pdfUrl = 'https://your-hosted-cv-url.pdf'
  window.open(pdfUrl, '_blank')
}
